import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AuthService,
  DialogService,
  InputErrorComponent,
  tr,
  DialogResult,
  Key,
  NullEntity,
  EntityDetailsGeneralBaseComponent,
  NotFoundEntity,
  decapitalize,
} from '@kpd/framework-ngx';
import { User } from 'app/shared/models/user';
import { UsersNavigatorHelper } from 'app/users/users-navigator-helper';
import { UserValidators } from 'app/shared/models/validators/user-validators';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationProviders, getAuthProviderName } from 'app/shared/enums/authentication-providers';
import { Tenant } from 'app/shared/models/tenant';
import { TenantService } from 'app/shared/services/tenant.service';
import { UserTypeService, IUserType } from 'app/shared/services/user-type.service';
import { Permissions } from 'app/shared/services/permissions';
import { UserService } from 'app/shared/services/user.service';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserFacade } from 'app/shared/services/facades/user.facade';
import { UMLocalizationService } from 'app/shared/services/localization.service';

const defaultUserType = AuthenticationProviders.azureAd;

@Component({
  selector: 'app-user-details-general',
  templateUrl: './user-details-general.component.html',
})
export class UserDetailsGeneralComponent
  extends EntityDetailsGeneralBaseComponent
  implements OnInit, AfterViewInit, OnChanges
{
  Permissions: typeof Permissions = Permissions;
  @Input()
  item: User;
  @Output()
  userCreated = new EventEmitter<string>();
  tenant: Tenant = new Tenant();
  cultures: { name: string; display: any }[] = [];
  userType: IUserType;
  allowedUserTypes: IUserType[] = [];
  warningMessage: string | null;
  canEditMode = true;
  hideDelete = false;
  hideCreate = false;
  userTypes: IUserType[] = this.userTypeService.getUserTypes();
  languageColumns = [{ name: 'display', title: tr('Language') }];
  upnConflict: { upn: string; message: string; otherTenant: boolean } | null;
  allowedPhoneCharacters = /[\d\-\+\/\*\(\)\#\|\.\;\,\~\s]*/;

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  get hasUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.UserManage);
  }

  get isSystemUser(): boolean {
    return this.item.isSystemUser || !!this.form.controls.isSystemUser.value;
  }

  constructor(
    private userService: UserService,
    private userFacade: UserFacade,
    private tenantService: TenantService,
    protected navigatorHelper: UsersNavigatorHelper,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private dialogService: DialogService,
    private userTypeService: UserTypeService,
    localizationService: UMLocalizationService,
    private activatedRoute: ActivatedRoute,
  ) {
    super(localizationService as any, activatedRoute);
  }

  ngOnInit(): void {
    this.setCultures();
    this.activatedRoute.data.subscribe((data) => {
      // When navigating to the "/me" route
      if (data.currentUser) {
        this.hideDelete = true;
        this.hideCreate = true;
      }
    });
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.form = this.createForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item) {
      this.upnConflict = null;
      this.canEditMode = true;
      this.warningMessage = null;
      this.setCultures();
      this.sanitizeCulture();
      if (this.item instanceof NullEntity && this.item.isNew()) {
        this.tenantService.getTenant().subscribe((tenant: Tenant) => {
          this.tenant = tenant;

          this.allowedUserTypes = this.userTypes.filter((type) => {
            return (
              type.authProviders &&
              type.authProviders.includes(AuthenticationProviders[decapitalize(this.tenant.authenticationProviders)])
            );
          });
          this.userType = this.allowedUserTypes.find((type) => type.id === defaultUserType) || this.allowedUserTypes[0];
          this.onUserTypeToggled();
        });
      }

      if (this.item.culture && !this.cultures.some((culture) => culture.name === this.item.culture)) {
        this.cultures.push({ name: this.item.culture, display: `${tr('Unsupported')} (${this.item.culture})` });
      }
      if (this.item instanceof User && !this.isLoggedInUser()) {
        if (
          !this.item.isDefaultTenant(this.authService.getTenant().key.toString()) &&
          !this.readOnlyMode &&
          !this.item.isNew()
        ) {
          this.canEditMode = false;
          this.warningMessage = tr('This user can not be edited, because it is managed by another tenant.');
        }
      }
    }
    super.ngOnChanges(changes);
  }

  sanitizeCulture() {
    if (this.item.culture) {
      const lang = this.item.culture.substring(0, this.item.culture.indexOf('-'));
      const country = this.item.culture.substring(this.item.culture.indexOf('-') + 1).toUpperCase();
      this.item.culture = `${lang}-${country}`;
    }
  }

  private setCultures(): void {
    if (this.cultures.length <= 0) {
      this.cultures = Object.keys(UMLocalizationService.getCultures()).map((cultureKey) => ({
        name: cultureKey,
        display: UMLocalizationService.getCultures()[cultureKey],
      }));
    }
  }

  updateUserDisplayName(): void {
    const newDisplayValue = User.composeDisplayName(
      (this.form.get('firstName') as AbstractControl).value,
      (this.form.get('lastName') as AbstractControl).value,
    );
    this.form.patchValue({ displayName: newDisplayValue });
  }

  createForm(): FormGroup {
    const form = this.formBuilder.group(
      {
        upn: new FormControl(this.item.upn, [
          Validators.required,
          Validators.maxLength(200),
          UserValidators['emailWithCustomError']('upn'),
        ]),
        firstName: new FormControl(this.item.firstName, [Validators.maxLength(100)]),
        lastName: new FormControl(this.item.lastName, [Validators.maxLength(200)]),
        displayName: new FormControl(this.item.displayName, [Validators.required, Validators.maxLength(300)]),
        email: new FormControl(this.item.email, [Validators.maxLength(200), UserValidators.email]),
        phone: new FormControl(this.item.phone, [Validators.maxLength(50)]),
        isSystemUser: new FormControl(this.item.isSystemUser),
        culture: new FormControl(
          this.cultures.find((culture) => culture.name === this.item.culture),
          Validators.required,
        ),
        password: new FormControl(this.item.password, [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(50),
        ]),
        confirmPassword: new FormControl(this.item.confirmPassword, [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(50),
        ]),
      },
      { validator: UserValidators.matchPassword },
    );
    this.updatePasswordValidatorRegistration(form);

    // User Change of firstName or lastName: update displayName
    (form.get('firstName') as AbstractControl).valueChanges.subscribe(() => {
      this.updateUserDisplayName();
    });
    (form.get('lastName') as AbstractControl).valueChanges.subscribe(() => {
      this.updateUserDisplayName();
    });
    (form.get('email') as AbstractControl).valueChanges.subscribe((value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue !== value) {
        (form.get('email') as AbstractControl).patchValue(trimmedValue);
      }
    });
    (form.get('upn') as AbstractControl).valueChanges.subscribe((value: string) => {
      this.upnConflict = null;
      const trimmedValue = value.trim();
      if (trimmedValue !== value) {
        (form.get('upn') as AbstractControl).patchValue(trimmedValue);
      }
      if (!(form.get('email') as AbstractControl).touched) {
        (form.get('email') as AbstractControl).patchValue(trimmedValue);
        (form.get('email') as AbstractControl).markAsDirty();
      }
    });

    return form;
  }

  onUserTypeToggled(): void {
    this.updatePasswordValidatorRegistration(this.form);
    if (this.item.isNew() && this.userType && this.userType.id === AuthenticationProviders.azureAd) {
      this.warningMessage = tr(
        'Creating an Active Directory user: the user data will be copied from Active Directory after the first login',
      );
    } else {
      this.warningMessage = '';
    }
  }

  updatePasswordValidatorRegistration(form: FormGroup): void {
    if (!form) {
      return;
    }

    if (this.userType && this.userType.passwordNeeded && this.item instanceof NullEntity && this.item.isNew()) {
      form.controls['password'].setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(50)]);
      form.controls['password'].updateValueAndValidity();
      form.controls['confirmPassword'].setValidators([
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(50),
      ]);
      form.controls['confirmPassword'].updateValueAndValidity();
    } else {
      form.controls['password'].reset();
      form.controls['password'].clearValidators();
      form.controls['password'].updateValueAndValidity();
      form.controls['confirmPassword'].reset();
      form.controls['confirmPassword'].clearValidators();
      form.controls['confirmPassword'].updateValueAndValidity();
    }
  }

  formToEntity(): User {
    const user: User = this.item instanceof NullEntity ? new User() : this.item.clone();
    user.upn = this.form.value.upn;
    user.firstName = this.form.value.firstName;
    user.lastName = this.form.value.lastName;
    user.displayName = this.form.value.displayName;
    user.email = this.form.value.email;
    user.culture = this.form.value.culture ? this.form.value.culture.name : undefined;
    user.phone = this.form.value.phone;
    user.isSystemUser = this.form.value.isSystemUser;
    if (this.userType && this.userType.passwordNeeded) {
      user.password = this.form.value.password;
    }
    if (this.item.isNew()) {
      user.isEnabled = true;
      // initialize the authentication provider
      if (!user.isSystemUser) {
        user.allowedAuthenticationProviders = this.userType.id;
      } else {
        user.allowedAuthenticationProviders = AuthenticationProviders.local;
      }
    }
    return user;
  }

  isValid(form: FormGroup): boolean {
    if (!form.dirty) {
      return true;
    }

    if (form.invalid) {
      this.setFieldErrors(true);
      return false;
    }

    return true;
  }

  executeSave(form: FormGroup): void {
    this.updater
      .saveEntity(this.formToEntity(), this.item.isNew())
      .pipe(
        // Check for user in other tenant
        catchError((err: HttpErrorResponse) => {
          if (err && err.status === 400) {
            InputErrorComponent.errorMessages['password'] = err['details'].error[0];
            this.form.controls.password.setErrors({ password: true });
            this.form.controls.confirmPassword.setErrors({ password: true });
          }
          if (err && err.status === 409) {
            return this.userService.getById(new Key<string>(this.form.value.upn)).pipe(
              tap((user) => {
                if (user instanceof NotFoundEntity) {
                  (form.get('upn') as any).setErrors({
                    userOtherTenant: true,
                  });
                  this.setFieldErrors(true);
                  this.upnConflict = {
                    upn: this.form.value.upn,
                    message: tr('A user with this UPN already exists in an other tenant.'),
                    otherTenant: true,
                  };
                  return;
                }
                (form.get('upn') as any).setErrors({
                  userUpnConflict: true,
                });
                this.setFieldErrors(true);
                this.upnConflict = {
                  upn: this.form.value.upn,
                  message: tr('A user with this UPN already exists.'),
                  otherTenant: false,
                };
              }),
            );
          }
          return of();
        }),
      )
      .subscribe(() => {
        this.form.markAsPristine();
      });
  }

  onRevert(): void {
    if (!this.form.dirty) {
      return;
    }
    this.updater.confirmAndRevert(this.item).subscribe((result: DialogResult) => {
      if (result === DialogResult.Verify) {
        this.form = this.createForm();
      }
    });
  }

  toggleReadOnly(): void {
    super.toggleReadOnly();
    this.warningMessage = null;
    this.canEditMode = true;
    if (this.item instanceof User && !this.isLoggedInUser()) {
      if (!this.item.isDefaultTenant(this.authService.getTenant().key.toString()) && !this.item.isNew()) {
        this.canEditMode = false;
        this.warningMessage = tr('This user can not be edited, because it is managed by another tenant.');
      }
    }
  }

  onAdd(): void {
    if (this.form.invalid) {
      this.setFieldErrors(true);
    }

    this.navigateToCreateUser();
  }

  getAuthProviders(user: User): string[] {
    return getAuthProviderName(user.allowedAuthenticationProviders || 0).map((name) => tr(name));
  }

  navigateToCreateUser(): void {
    this.navigatorHelper.navigateToAdd(true, true);
  }

  changePassword(): void {
    this.dialogService
      .open('changePassword', {
        header: tr('Change password'),
        verifyButtonText: tr('Change'),
        cancelButtonText: tr('Cancel'),
        userKey: this.item.key,
        localUser: this.hasUserManagePermission,
      })
      .afterClosed()
      .subscribe();
  }

  isLoggedInUser(): boolean {
    if (!this.item || !this.item.key) {
      return false;
    }
    return this.item.key.toString().toLowerCase() === this.authService.getIdentity().upn.toLowerCase();
  }

  linkUserToTenant(upn: string): void {
    this.form.markAsPristine();
    this.userFacade.detailLoadingStarted();
    this.userService.addUserToTenant(new Key<string>(upn)).subscribe(
      () => {
        this.userFacade.detailLoadingFinished();
        this.navigatorHelper.navigateToConsult(new Key<string>(upn), true, true);
      },
      () => {
        this.userFacade.detailLoadingFinished();
      },
    );
  }

  navigateToUser(upn: string): void {
    this.navigatorHelper.navigateToConsult(new Key<string>(upn), true, true);
  }

  phoneMask(rawValue: string): any[] {
    const arraylength = rawValue.length;
    const regexp = /[\d\-\+\/\*\(\)\#\|\.\;\,\~\s]/;
    const arr = [];
    for (let i = 0; i <= arraylength; i++) {
      if (regexp.test(rawValue.charAt(i))) {
        arr.push(regexp);
      }
    }
    return arr;
  }

  unlockUser(): void {
    this.userFacade.unlockUser(this.item).subscribe(() => (this.item.isLockedOut = false));
  }

  onSystemUserChanged(): void {
    if (this.isSystemUser) {
      this.userType = this.userTypes.find((u) => u.id === AuthenticationProviders.local) as IUserType;
    } else if (!this.allowedUserTypes.some((u) => u.id === AuthenticationProviders.local)) {
      this.userType = this.userTypes.find((u) => u.id === AuthenticationProviders.azureAd) as IUserType;
    }
  }
}
